import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { HOLIDAY } from "@/lib/colors";
import { todayStr } from "@/lib/dates";
import { ClashWarning } from "@/components/holidays/ClashWarning";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const blank = () => ({ start_date: todayStr(), end_date: todayStr(), type: "annual", note: "" });

export const HolidayDialog = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState(blank());
  const [clashes, setClashes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(blank());
  }, [open]);

  useEffect(() => {
    if (!open || !form.start_date || !form.end_date || form.start_date > form.end_date) {
      setClashes([]);
      return;
    }
    api.get("/holidays/clashes", { params: { start_date: form.start_date, end_date: form.end_date } }).then((r) => setClashes(r.data)).catch(() => setClashes([]));
  }, [open, form.start_date, form.end_date]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/holidays", form);
      toast.success("Request sent to your supervisor");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="holiday-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#24474A]">Request time off</DialogTitle>
          <DialogDescription>Approved leave shows on everyone's calendar so the team can plan around it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="holiday-start-input" /></div>
            <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={form.end_date} min={form.start_date} onChange={set("end_date")} data-testid="holiday-end-input" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(HOLIDAY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={3} value={form.note} onChange={set("note")} placeholder="Optional" data-testid="holiday-note-input" /></div>
          <ClashWarning clashes={clashes} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full" onClick={submit} disabled={busy} data-testid="holiday-submit-btn">Submit request</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
